import { Box, Flex } from '@chakra-ui/react';
import { useEffect } from 'react';
import DocumentTitle from '~/components/DocumentTitle';
import { useGetAgency } from '~/hooks/api/agencies/get';
import { MediaKit } from '~/hooks/api/media-kits/get';
import { ComponentRenderer } from '~/screens/PageBuilder/canvas/ComponentRenderer';

import { AVAILABLE_FONTS } from '../AgencySettingsScreen/components/GeneralTab/components/AgencyBranding/constants';
import {
  applyMediaKitFont,
  getMediaKitColors,
} from '../AgencySettingsScreen/components/GeneralTab/components/AgencyBranding/utils';
import { TopBar } from './Topbar';

export const MediaKitPreview = ({ data }: { data: MediaKit }) => {
  const agency = useGetAgency(data.agency_id);

  useEffect(() => {
    if (!agency.data?.theming) return;

    const brandPrimaryColor = agency.data?.theming?.primaryColor || '';
    const brandSecondaryColor = agency.data?.theming?.secondaryColor || '';
    const brandBackgroundColor =
      agency.data?.theming?.backgroundColor || '#fff';
    const brandFont = agency.data?.theming?.font;

    const mkColors = getMediaKitColors({
      primaryColor: brandPrimaryColor,
      secondaryColor: brandSecondaryColor,
      backgroundColor: brandBackgroundColor,
    });
    Object.entries(mkColors).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value as string);
    });

    if (brandBackgroundColor) {
      // update meta theme color
      document
        .querySelector('meta[name="theme-color"]')
        ?.setAttribute('content', brandBackgroundColor);

      // Update document background color to prevent awkward white flash while scrolling
      document.body.style.backgroundColor =
        mkColors['--media-kit-background-primary'];
    }

    // Load font if specified in media kit data
    const font = AVAILABLE_FONTS.find((f) => f.name === brandFont);
    applyMediaKitFont(font);
  }, [agency.data]);

  return (
    <>
      <DocumentTitle>{data.name}</DocumentTitle>

      <Box
        as="main"
        maxWidth={1200}
        marginX="auto"
        paddingX={{
          base: 6,
          lg: 0,
        }}
        style={{ fontFamily: 'var(--media-kit-font-family, inherit)' }}
      >
        <TopBar />
        <Flex
          flexDirection="column"
          gap={10}
          color="var(--media-kit-text-base)"
        >
          {data.blocks.map((block) => (
            <ComponentRenderer block={block} key={block.id} readonly />
          ))}
        </Flex>
      </Box>
    </>
  );
};
